import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../redux/auth/authSlice";
import service from "../services/service";
import endpoints from "../services/endpoints";
import {
  DropdownIcon,
  Lock,
  Logout,
  Notification,
  Search,
} from "../assests/ExportImageIcon";
import ChangePasswordModal from "../componants/changePassword/ChangePasswordModal";
import "./header.css";

const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email ? email[0].toUpperCase() : "A";
};

const ROLE_LABEL = { 1: "Super Admin", 2: "Admin" };

const Header = ({ onToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);
  const refresh_token = useSelector((state) => state.auth.refresh_token);

  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const notificationTimeoutRef = useRef(new Map());
  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const accessToken = useSelector((state) => state?.auth?.access_token);

  useEffect(() => {
    console.log("===== NOTIFICATION WS START =====");

    console.log("Access Token:", accessToken ? "AVAILABLE" : "MISSING");

    const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;

    console.log("WS Base URL:", wsBaseUrl || "MISSING");

    if (!accessToken) {
      console.error("Notification WebSocket: accessToken missing");
      return;
    }

    if (!wsBaseUrl) {
      console.error("Notification WebSocket: REACT_APP_WS_BASE_URL is missing");
      return;
    }

    const wsUrl = `${wsBaseUrl}/notifications/?token=${encodeURIComponent(accessToken)}`;

    console.log("Connecting WebSocket:", wsUrl);

    const ws = new WebSocket(wsUrl);

    wsRef.current = ws;

    ws.onopen = () => {
      console.log("✅ Notification WebSocket connected");
    };

    ws.onmessage = (event) => {
      console.log("📩 WebSocket message received:", event.data);

      try {
        const response =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        console.log("Parsed WebSocket response:", response);

        if (response?.type !== "notification") {
          return;
        }

        const notification = response?.notification;

        if (!notification) {
          return;
        }

        console.log("🔔 Live notification received:", notification);

        setHasUnreadNotification(true);

        const toastId = `${Date.now()}-${Math.random()}`;

        const liveToast = {
          ...notification,
          toastId,
        };

        setLiveNotifications((prev) => [...prev, liveToast]);

        const timeoutId = setTimeout(() => {
          setLiveNotifications((prev) =>
            prev.filter((item) => item.toastId !== toastId),
          );

          notificationTimeoutRef.current.delete(toastId);
        }, 50000);

        notificationTimeoutRef.current.set(toastId, timeoutId);
      } catch (error) {
        console.error("Notification WebSocket message error:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("❌ Notification WebSocket error:", error);
    };

    ws.onclose = (event) => {
      console.log(
        "❌ Notification WebSocket disconnected",
        event.code,
        event.reason,
      );
    };

    return () => {
      console.log("Cleaning Notification WebSocket");

      ws.close();
      if (wsRef.current === ws) {
        wsRef.current = null;
      }

      notificationTimeoutRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });

      notificationTimeoutRef.current.clear();
    };
  }, [accessToken]);

  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  const handleLogoutClick = () => {
    setShowDropdown(false);
    setShowConfirm(true);
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await service.post(endpoints.auth.logout, {
        refresh: refresh_token,
        device_id: "",
      });
    } catch {
      // proceed with logout even if API fails
    } finally {
      dispatch(logout());
      navigate("/", { replace: true });
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const initials = getInitials(user?.full_name, user?.email);
  const roleLabel = ROLE_LABEL[user?.user_type] || "Admin";

  return (
    <>
      {/* ── Confirm logout modal ── */}
      {showConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="confirm-actions">
              <button
                className="commonBtnTransparent"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="commonBtnRed"
                onClick={handleSignOut}
                disabled={loading}
              >
                {loading ? "Logging out..." : "Logout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
      {liveNotifications.map((notification) => (
        <div className="liveNotificationToast" key={notification.toastId}>
          <div className="liveNotificationIcon">
            <img src={Notification} alt="notification" />
          </div>

          <div className="liveNotificationContent">
            <div className="liveNotificationTitle">
              {notification?.title || "New Notification"}
            </div>

            <div className="liveNotificationMessage">
              {notification?.message || ""}
            </div>
          </div>

          <button
            className="liveNotificationClose"
            onClick={() => {
              const timeoutId = notificationTimeoutRef.current.get(
                notification.toastId,
              );

              if (timeoutId) {
                clearTimeout(timeoutId);
                notificationTimeoutRef.current.delete(notification.toastId);
              }

              setLiveNotifications((prev) =>
                prev.filter((item) => item.toastId !== notification.toastId),
              );
            }}
          >
            ×
          </button>
        </div>
      ))}
      <header className="top-header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={onToggle}>
            ☰
          </button>
          <div className="searchPart">
            <img src={Search} alt="search" />
            <input
              type="Search"
              placeholder="Search bookings, vendors, customers..."
            />
          </div>
        </div>

        <div className="header-right">
          <div
            onClick={() => {
              setHasUnreadNotification(false);
              navigate("/notification");
            }}
            className="notification"
          >
            <img src={Notification} alt="notification" />
            <span
              className="redBg"
              style={{
                display: hasUnreadNotification ? "block" : "none",
              }}
            ></span>
          </div>

          {/* ── Profile dropdown ── */}
          <div className="profile-dropdown-wrapper" ref={dropdownRef}>
            <div
              className="profile-trigger"
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              <div className="profile-avatar">{initials}</div>
              <div className="profile-info">
                <span className="profile-name">
                  {user?.full_name || user?.email || "Admin"}
                </span>
                <span className="profile-email">{roleLabel}</span>
              </div>
              <img src={DropdownIcon} alt="dropdown" />
            </div>

            {showDropdown && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <span className="dropdown-label">Signed in as</span>
                  <span className="dropdown-email">{user?.email || "—"}</span>
                </div>
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item d-flex"
                  onClick={() => {
                    setShowDropdown(false);
                    setShowChangePassword(true);
                  }}
                >
                  <img
                    src={Lock}
                    alt=""
                    style={{
                      width: 16,
                      marginRight: 8,
                      verticalAlign: "middle",
                    }}
                  />
                  Change Password
                </button>
                <button
                  className="dropdown-item d-flex"
                  onClick={handleLogoutClick}
                >
                  <img
                    src={Logout}
                    alt=""
                    style={{
                      width: 16,
                      marginRight: 8,
                      verticalAlign: "middle",
                    }}
                  />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
